import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { openPaystackPopup } from "@/lib/paystack";
import { useAuth } from "@/context/AuthContext";
import arcLogo from "@/assets/arc-logo.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ThemeToggle } from "@/components/Layout";
import { toast } from "sonner";
import { Gift, Loader2, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { motion } from "framer-motion";

export default function Register() {
  const { user, authenticate } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ username: "", phone: "", password: "", referral_code: "", region: "NG" });
  const [submitting, setSubmitting] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    const ref = params.get("ref");
    if (ref) setForm((f) => ({ ...f, referral_code: ref }));
  }, [params]);

  useEffect(() => {
    if (user) nav("/dashboard");
  }, [user, nav]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e, payNow = false) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Creates the profile (starts as a trial) and logs the user in.
      const { data } = await api.post("/auth/register", { ...form });
      authenticate(data.token, data.user);
      if (!payNow) {
        toast.success("Welcome! Your 3-day free trial has started.");
        nav("/dashboard");
        return;
      }
      // Direct-to-membership: open the Paystack popup immediately to pay ₦3,000.
      const callback_url = `${window.location.origin}/payment/callback`;
      const { data: init } = await api.post("/payment/initialize", { callback_url });
      openPaystackPopup(init, {
        onSuccess: () => nav(`/payment/callback?reference=${init.reference}`),
        onCancel: () => { toast.message("Payment not completed — you're on the free trial. Activate anytime."); nav("/dashboard"); },
      });
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute -top-24 -right-24 h-80 w-80 navy-gradient opacity-25 blur-3xl rounded-full" />
        <div className="flex items-center gap-2 relative">
          <img src={arcLogo} alt="Art Review Club" className="h-9 w-9 rounded-xl object-contain" />
          <span className="font-heading text-xl font-bold">Art Review Club</span>
        </div>
        <div className="relative">
          <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-gold bg-white/10 px-3 py-1.5 rounded-full">
            <Gift className="h-3.5 w-3.5" /> 3-Day Free Trial
          </span>
          <h2 className="font-heading text-4xl font-bold mt-5 leading-tight">Try AR Club free for 3 days.</h2>
          <p className="text-primary-foreground/80 mt-4 max-w-sm">Explore the gallery and try 2 daily art reviews free. Activate membership anytime to unlock rewards, holdings and your instant ₦500 Welcome Cash-back.</p>
        </div>
        <p className="text-xs text-primary-foreground/60 relative">No payment to start • Upgrade anytime</p>
      </div>

      <div className="flex flex-col p-6 md:p-12">
        <div className="flex items-center justify-between">
          <button onClick={() => nav("/")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Home</button>
          <ThemeToggle />
        </div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full">
          <>
            <h1 className="font-heading text-3xl font-bold">Start your free trial</h1>
              <p className="text-muted-foreground mt-1">No payment needed to begin exploring AR Club.</p>
              <form onSubmit={submit} className="space-y-4 mt-6">
                <div>
                  <Label htmlFor="username">Username</Label>
                  <Input id="username" data-testid="reg-username" value={form.username} onChange={set("username")} required className="h-12 rounded-xl mt-1.5" placeholder="e.g. adaeze" />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input id="phone" data-testid="reg-phone" value={form.phone} onChange={set("phone")} required className="h-12 rounded-xl mt-1.5" placeholder="e.g. 08012345678" />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative mt-1.5">
                    <Input id="password" data-testid="reg-password" type={showPw ? "text" : "password"} value={form.password} onChange={set("password")} required className="h-12 rounded-xl pr-12" placeholder="Min. 6 characters" />
                    <button type="button" onClick={() => setShowPw((s) => !s)} data-testid="reg-toggle-password" aria-label={showPw ? "Hide password" : "Show password"} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="ref">Referral Code <span className="text-muted-foreground">(optional)</span></Label>
                  <Input id="ref" data-testid="reg-referral" value={form.referral_code} onChange={set("referral_code")} className="h-12 rounded-xl mt-1.5" placeholder="Enter code" />
                </div>
                <div className="flex gap-3">
                  <Button type="submit" disabled={submitting} data-testid="reg-submit"
                    className="flex-1 basis-0 min-w-0 h-12 px-2 rounded-full navy-gradient text-white text-sm sm:text-base font-semibold whitespace-nowrap shadow-[0_5px_0_0_#060f2e] hover:shadow-[0_5px_0_0_#060f2e] hover:brightness-110 active:translate-y-[3px] active:shadow-[0_2px_0_0_#060f2e] transition-[transform,box-shadow,filter]">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "3-Day Trial"}
                  </Button>
                  <Button type="button" onClick={(e) => submit(e, true)} disabled={submitting} data-testid="reg-pay-now"
                    className="flex-1 basis-0 min-w-0 h-12 px-2 rounded-full text-white text-sm sm:text-base font-semibold whitespace-nowrap bg-[#4c1d95] hover:bg-[#5b21b6] border-0 shadow-[0_5px_0_0_#2e1065] active:translate-y-[3px] active:shadow-[0_2px_0_0_#2e1065] transition-[transform,box-shadow,background-color]">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue to Payment"}
                  </Button>
                </div>
                <p className="text-center text-[11px] text-muted-foreground">Try free for 3 days, or pay now to unlock full membership instantly with ₦500 cash-back.</p>
              </form>
              <p className="text-center text-sm text-muted-foreground mt-6">
                Already a member? <button onClick={() => nav("/login")} className="text-gold font-semibold" data-testid="goto-login">Log in</button>
              </p>
            </>
        </motion.div>
      </div>
    </div>
  );
}
